import { ChangeDetectorRef, Component, computed, forwardRef, inject, Input, input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConfiguredActionItemDto, ConfiguredActionService } from '@artemis-software/wr-api';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { catalogTenantId } from '@/utils/admin-utils';
import { asyncComputed } from '@/utils/signals/asyncComputed';

// Spiegelt String.normalizedActionName() im Backend (ConfiguredAction.kt)
const normalizeActionName = (name: string): string => name.trim().toLowerCase();

@Component({
  standalone: false,
  selector: 'wr-action-select',
  templateUrl: './action-select.component.html',
  styleUrls: ['./action-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ActionSelectComponent),
      multi: true,
    },
  ],
})
export class ActionSelectComponent extends AbstractFormControl<ConfiguredActionItemDto | undefined> {
  private readonly configuredActionService = inject(ConfiguredActionService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly currentTenantId = catalogTenantId();

  showAllOption = input(false);

  tenantId = input<string | undefined>();

  // Zusätzliche Maßnahmen, die im Datenbestand tatsächlich vorkommen. Die tenant-konfigurierte
  // Liste allein reicht nicht: eine Maßnahme hängt am Tenant des Prüfpunkts, der aufrufende
  // Kontext kennt oft nur einen anderen Tenant (WR-2317). Leer für alle Aufrufer außer der
  // Prüfpunktzeilen-Statistik — deren Verhalten bleibt dadurch unverändert.
  readonly additionalActions = input<ConfiguredActionItemDto[]>([]);

  private readonly tenantActions = asyncComputed(
    () => this.configuredActionService.findAll({ tenantId: this.tenantId() ?? this.currentTenantId() }),
    [] as ConfiguredActionItemDto[],
  );

  readonly actions = computed(() => {
    const merged = [...this.tenantActions()];
    // Dublettenschutz über den NAMEN, nicht nur über die id: dieselbe Maßnahme existiert je
    // internem Tenant als eigene Zeile, sonst stünde „Maßnahmen erforderlich" mehrfach im
    // Dropdown — gleiche Beschriftung, andere Ergebnismenge. Der Server filtert für eine
    // Auswahl über alle gleichnamigen Maßnahmen, der Repräsentant ist also austauschbar.
    const seen = new Set(merged.map((it) => normalizeActionName(it.name)));
    for (const action of this.additionalActions()) {
      const key = normalizeActionName(action.name);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(action);
    }
    return merged.sort((a, b) => a.ranking - b.ranking || a.name.localeCompare(b.name));
  });

  private _recommendedValue?: ConfiguredActionItemDto;

  @Input()
  set recommendedValue(value: ConfiguredActionItemDto | undefined) {
    this._recommendedValue = undefined;
    this.cdr.detectChanges();
    setTimeout(() => {
      this._recommendedValue = value;
      this.cdr.detectChanges();
    });
  }

  get recommendedValue(): ConfiguredActionItemDto | undefined {
    return this._recommendedValue;
  }

  get actionsWithoutRecommended(): ConfiguredActionItemDto[] {
    return this.actions().filter((action) => action.id !== this.recommendedValue?.id);
  }

  get fallbackAction(): ConfiguredActionItemDto | undefined {
    const selected = this.value;
    if (!selected || selected.id === this.recommendedValue?.id) {
      return undefined;
    }
    return this.actions().some((action) => action.id === selected.id) ? undefined : selected;
  }

  compareById = (a?: ConfiguredActionItemDto, b?: ConfiguredActionItemDto): boolean => a?.id === b?.id;
}
